<div class="pit">
  @if (state.overviewLoading() && !state.overview()) {
    <div class="pit__state">
      <app-cog-loading-spinner [size]="56" />
      <p class="pit-text">Carregando as declarações dos sócios...</p>
    </div>
  } @else if (state.overviewError(); as error) {
    <div class="pit__state">
      <i class="pi pi-exclamation-circle pit__state-icon" aria-hidden="true"></i>
      <p class="pit-text">{{ error }}</p>
      <app-button
        [label]="'Tentar de novo'"
        [fitContent]="true"
        (sendButtonClick)="state.reload()" />
    </div>
  } @else if (state.overview(); as overview) {
    <div class="pit__base">
      <div class="pit__base-info">
        <span class="pit__eyebrow">Base</span>
        @if (baseRange(); as range) {
          <span class="pit-tag pit-tag--navy">{{ range }}</span>
        }
        @if (folderLink(); as link) {
          <span class="pit-caption">
            Fonte:
            @if (canOpenDataRoom()) {
              <a class="pit-link" [routerLink]="link.route" [queryParams]="link.queryParams">{{
                link.label
              }}</a>
            } @else {
              {{ link.label }}
            }
          </span>
        }
        <!--
          Uma linha só de contexto (Beleza, ritmo): base, fonte, aviso curto da política e resumo do Data Room. Um aviso
          só para a falta de política: as colunas e os KPIs que dependem dela somem (régua 5).
        -->
        @if (!policyDefined() && !isEmpty()) {
          <span
            class="pit__context pit__context--warning"
            role="note"
            title="Sem política de garantias definida: a tela não calcula quanto de cada bem serve de garantia.">
            <i class="pi pi-info-circle" aria-hidden="true"></i>
            Sem política de garantias
            <a *appHasRole="'ADMIN'" class="pit-link" [routerLink]="parametrizationRoute"
              >Definir</a
            >
          </span>
        }
        @if (documents.total()) {
          <span class="pit__context">
            <i class="pi pi-file" aria-hidden="true"></i>
            {{ documents.summary() }}
            <!-- "Ver" abre a aba interna Documentos (sem sócio não há abas: o bloco já aparece embaixo). -->
            @if (!isEmpty()) {
              <button type="button" class="pit-link" (click)="openDocuments()">Ver</button>
            }
          </span>
        }
      </div>
    </div>

    @if (readingProgress(); as progress) {
      <!-- Andamento da rodada (card 3772): números do backend, a barra só desenha a fração concluída. -->
      <div class="pit-callout pit__progress" role="status" aria-live="polite">
        <i class="pi pi-file-import pit__progress-icon" aria-hidden="true"></i>
        <div class="pit__progress-body">
          <p class="pit__progress-title">{{ progress.title }}</p>
          <!--
            Azul = lidos, vermelho = falharam, trecho pulsando = sendo lidos agora (sinal de que está andando). A barra
            conta o mesmo que o título: falha não pinta de azul.
          -->
          <div
            class="pit__progress-bar"
            role="progressbar"
            aria-label="Documentos lidos nesta leitura"
            [attr.aria-valuenow]="progress.readPercent"
            aria-valuemin="0"
            aria-valuemax="100">
            <span class="pit__progress-done" [style.width.%]="progress.readPercent"></span>
            <span class="pit__progress-failed" [style.width.%]="progress.failedPercent"></span>
            <span class="pit__progress-reading" [style.width.%]="progress.readingPercent"></span>
          </div>
          @if (progress.detail) {
            <p class="pit-caption">{{ progress.detail }}</p>
          }
        </div>
      </div>
    }

    @if (processingText(); as text) {
      <!-- Aviso de leitura pedida antes do card 3772 (sem rodada): mesmo ícone do aviso de andamento. -->
      <div class="pit-callout pit__progress" role="status">
        <i class="pi pi-file-import pit__progress-icon" aria-hidden="true"></i>
        <span>{{ text }}</span>
      </div>
    }

    @if (failedText(); as text) {
      <div class="pit-callout pit-callout--danger">
        <span>{{ text }}</span>
        @if (state.canUpdate()) {
          <app-button
            [label]="'Ler de novo'"
            [outlined]="true"
            [size]="'small'"
            [fitContent]="true"
            [disabled]="state.saving()"
            (sendButtonClick)="state.reprocess()" />
        }
      </div>
    }

    @if (isEmpty()) {
      <div class="pit__state pit__state--empty">
        <i class="pi pi-folder-open pit__state-icon" aria-hidden="true"></i>
        <p class="pit-strong">Nenhuma declaração de IR dos sócios no Data Room desta empresa</p>
        <p class="pit-text">
          Envie a declaração completa do IRPF de cada sócio (PDF gerado pelo programa da Receita, de
          preferência com o recibo de entrega) na pasta IRPF do Data Room. A leitura começa sozinha
          e cada sócio aparece aqui quando ela terminar.
        </p>
        @if (canOpenDataRoom()) {
          @if (folderLink(); as link) {
            <app-button
              class="pit__state-action"
              [label]="link.buttonLabel"
              [fitContent]="true"
              (sendButtonClick)="openFolder(link)" />
          }
        }
      </div>
      <!-- Sem sócio não há abas internas: documentos já enviados (não lidos, com erro) aparecem aqui embaixo. -->
      @if (documents.total()) {
        <app-pit-documents-section />
      }
    } @else {
      <app-pit-partner-selector
        [partners]="overview.partners"
        [selection]="state.selection()"
        [policyDefined]="policyDefined()"
        [debtsByPerson]="debtsByPerson()"
        (selectPartner)="state.selectPartner($event)"
        (selectConsolidated)="state.selectConsolidated()" />

      @switch (state.selection()?.kind) {
        @case ('consolidated') {
          @if (overview.consolidated; as consolidated) {
            <app-pit-consolidated-view
              [consolidated]="consolidated"
              [policyDefined]="policyDefined()"
              [fallbackDebtLimits]="state.partner()?.indicators?.debtsOverNetWorthLimits"
              [partners]="overview.partners"
              (openPartner)="openPartnerFromConsolidated($event)" />
          }
        }
        @case ('partner') {
          @if (state.partnerLoading() && !state.partner()) {
            <div class="pit__state">
              <app-cog-loading-spinner [size]="48" />
            </div>
          } @else if (state.partnerError(); as error) {
            <div class="pit__state">
              <i class="pi pi-exclamation-circle pit__state-icon" aria-hidden="true"></i>
              <p class="pit-text">{{ error }}</p>
            </div>
          } @else if (state.partner(); as partner) {
            <app-pit-partner-detail [partner]="partner" [policyDefined]="policyDefined()" />
          }
        }
      }
    }
  }
</div>
